import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
  Autocomplete,
  Chip,
} from '@mui/material';
import { Close, Send } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import { RootState } from '../store/index';
import { apiService } from '../services/api';
import { ChangeEmailPreview } from './ChangeEmailPreview';
import type { Change } from './ChangesTable';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ChangeEmailSendDialogProps {
  open: boolean;
  onClose: () => void;
  change: Change | null;
  onSent?: () => void;
}

function normalizeRecipients(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((raw) => {
    raw
      .split(/[,;]+/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((email) => {
        const key = email.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          result.push(email);
        }
      });
  });
  return result;
}

export function ChangeEmailSendDialog({
  open,
  onClose,
  change,
  onSent,
}: ChangeEmailSendDialogProps) {
  const { user } = useSelector((state: RootState) => state.auth);
  const queryClient = useQueryClient();
  const changeId = change?.id;

  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [sending, setSending] = useState(false);

  const { data: preview, isLoading: previewLoading } = useQuery(
    ['change-email-preview', changeId],
    () => apiService.getChangeEmailPreview(changeId!),
    { enabled: open && !!changeId, refetchOnWindowFocus: false },
  );

  useEffect(() => {
    if (!open) return;
    setEmailRecipients(user?.email ? [user.email] : []);
  }, [open, user?.email]);

  useEffect(() => {
    if (preview?.subject) {
      setEmailSubject(preview.subject);
    }
  }, [preview?.subject]);

  const handleSend = async () => {
    if (!changeId || emailRecipients.length === 0 || !emailSubject) return;

    const recipients = normalizeRecipients(emailRecipients);
    const invalid = recipients.filter((email) => !EMAIL_PATTERN.test(email));
    if (invalid.length > 0) {
      toast.error(`E-mail inválido: ${invalid.join(', ')}`);
      return;
    }

    setSending(true);
    try {
      const result = await apiService.sendChangeNotificationEmail({
        change_id: changeId,
        to: recipients,
        subject: emailSubject,
      });
      if (result.success) {
        toast.success(result.message || 'E-mail enviado com sucesso');
        void queryClient.invalidateQueries(['change-delivery-logs', changeId]);
        onSent?.();
        onClose();
      } else {
        toast.error('Falha ao enviar e-mail');
      }
    } catch {
      toast.error('Erro ao enviar e-mail. Tente novamente.');
    } finally {
      setSending(false);
    }
  };

  const previewData = preview
    ? {
        headline: preview.headline,
        title: preview.title,
        summary: preview.summary,
        document_url: preview.document_url,
        intro: preview.intro,
        marketing_signature: preview.marketing_signature,
        state: change?.state,
        agency: change?.agency,
        change_type: change?.change,
      }
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Enviar Mudança por E-mail
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Mesmo template do disparo automático (simulado em dev)
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={emailRecipients}
            onChange={(_, value) => setEmailRecipients(normalizeRecipients(value.map(String)))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={`${option}-${index}`} label={option} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Destinatários"
                placeholder="Digite um e-mail e pressione Enter"
                helperText="Um ou mais endereços (separe por vírgula ou Enter)"
                required
                autoFocus
              />
            )}
          />
          <TextField
            fullWidth
            label="Assunto"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            required
          />
          <ChangeEmailPreview preview={previewData} loading={previewLoading} />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={sending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          startIcon={sending ? undefined : <Send />}
          disabled={emailRecipients.length === 0 || !emailSubject || sending || !changeId}
        >
          {sending ? 'Enviando…' : 'Enviar E-mail'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
